<script lang="ts" setup>
import { computed } from 'vue';
import { useDisplay } from 'vuetify';
import ChevronLeftSvg from '@/assets/svg/chevron-left.svg?component';

const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    title?: string;
    subtitle?: string;
    showHeading?: boolean;
    showActions?: boolean;
    showCloseButton?: boolean;
    showCancelButton?: boolean;
    showSecondaryButton?: boolean;
    isLoading?: boolean;
    isSubmitDisabled?: boolean;
    cancelButtonText?: string;
    secondaryButtonText?: string;
    submitButtonText?: string;
    maxWidth?: string;
    maxHeight?: string;
    backButtonText?: string;
    /**
     * Opt-in: render the dialog fullscreen (edge-to-edge) on mobile (<md / smAndDown).
     * Default false so existing consumers are unchanged. When true on mobile the
     * fixed maxWidth/maxHeight are ignored, rounded corners drop, and the whole
     * card scrolls so header/actions stay reachable instead of being clipped.
     */
    fullscreenOnMobile?: boolean;
  }>(),
  {
    showHeading: true,
    showActions: true,
    cancelButtonText: 'Cancel',
    secondaryButtonText: 'Secondary',
    submitButtonText: 'Submit',
    maxWidth: '600px',
    maxHeight: '550px',
    fullscreenOnMobile: false,
  },
);

const display = useDisplay();
const isFullscreen = computed(() => props.fullscreenOnMobile && display.smAndDown.value);
// When fullscreen, drop the fixed width/height so Vuetify fills the viewport
// (an explicit :height would otherwise cap the dialog and leave it top-aligned).
const dialogMaxWidth = computed(() => (isFullscreen.value ? undefined : props.maxWidth));
const dialogHeight = computed(() => (isFullscreen.value ? undefined : props.maxHeight));

const emit = defineEmits<{
  (e: 'cancel'): void;
  (e: 'submit'): void;
  (e: 'secondary'): void;
  (e: 'back'): void;
  (e: 'update:modelValue', payload: boolean): void;
}>();

// Always `cancel`. It used to become `back` whenever backButtonText was set, which left a
// fullscreen-on-mobile modal with a subview (its only X lives in the sticky bar) with no
// way to close at all.
function cancel() {
  emit('cancel');
}

function back() {
  emit('back');
}

function submit() {
  emit('submit');
}

function secondary() {
  emit('secondary');
}

function handleUpdateModelValue(value: boolean) {
  emit('update:modelValue', value);
}
</script>

<template>
  <v-dialog
    :model-value="modelValue"
    :max-width="dialogMaxWidth"
    :height="dialogHeight"
    :fullscreen="isFullscreen"
    persistent
    @update:model-value="handleUpdateModelValue"
  >
    <div
      :class="[
        'bg-white',
        isFullscreen
          ? 'h-full overflow-y-auto px-4 pb-[max(1rem,env(safe-area-inset-bottom))]'
          : 'rounded-2xl p-8',
      ]"
    >
      <!--
        Fullscreen-only sticky close bar: gives every fullscreen modal an
        always-reachable X (independent of showCloseButton, stays put while the
        body scrolls) and carries the top safe-area inset (notch / status bar).
      -->
      <div
        v-if="isFullscreen"
        class="sticky top-0 z-10 -mx-4 flex justify-end bg-white px-4 pb-2 pt-[max(0.5rem,env(safe-area-inset-top))]"
      >
        <button
          type="button"
          aria-label="Close"
          class="-mr-2 flex h-11 w-11 cursor-pointer items-center justify-center"
          data-test="base-modal-close-fullscreen"
          @click="cancel"
        >
          <img src="@/assets/svg/close-black.svg" alt="" width="20" height="20" />
        </button>
      </div>

      <slot name="header">
        <div v-if="showHeading">
          <!-- Close button (non-fullscreen only; fullscreen uses the sticky bar above) -->
          <img
            v-if="showCloseButton && !isFullscreen"
            class="float-right mt-1 cursor-pointer"
            src="@/assets/svg/close-black.svg"
            alt="close"
            width="20"
            height="20"
            data-test="base-modal-close"
            @click="cancel"
          />
          <!-- Back button -->
          <v-btn
            v-if="backButtonText"
            :ripple="false"
            class="modal-button-text -mb-1"
            variant="flat"
            density="compact"
            data-test="base-modal-back"
            @click="back"
          >
            <ChevronLeftSvg class="mr-1.5" /> {{ backButtonText }}
          </v-btn>
          <!-- Title -->
          <div v-if="title" class="text-left font-serif text-2xl font-black text-copilot">
            {{ title }}
          </div>

          <!-- Subtitle -->
          <div v-if="subtitle" class="text-base text-shade-800">{{ subtitle }}</div>
        </div>
      </slot>

      <!-- Default Slot -->
      <slot />

      <slot name="actions">
        <div
          v-if="showActions"
          :class="{
            'mt-4 flex items-center gap-4': true,
            'justify-between': showSecondaryButton && showCancelButton,
            'justify-end': !(showSecondaryButton && showCancelButton),
          }"
        >
          <!-- Cancel button -->
          <v-btn
            v-if="showCancelButton"
            :ripple="false"
            class="modal-button-text"
            variant="flat"
            @click="cancel"
            >{{ cancelButtonText }}</v-btn
          >

          <div class="flex items-center justify-end gap-4">
            <!-- Secondary button -->
            <v-btn
              v-if="showSecondaryButton"
              :ripple="false"
              class="modal-button-secondary"
              variant="flat"
              @click="secondary"
              >{{ secondaryButtonText }}</v-btn
            >

            <!-- Submit button -->
            <v-btn
              :ripple="false"
              :loading="isLoading"
              data-test="base-modal-submit"
              :disabled="isSubmitDisabled"
              class="modal-button-primary"
              variant="flat"
              @click="submit"
              >{{ submitButtonText }}</v-btn
            >
          </div>
        </div>
      </slot>
    </div>
  </v-dialog>
</template>
